import { Injectable } from '@angular/core';
import { UserBasicInfo } from '../model/user/UserBasicInfo';
import RolesHierarchy from '../model/user/RolesHierarchy';
import Roles from 'src/app/shared/model/user/Roles';
import { PushIdentityService } from './push-identity.service';

@Injectable()
export class UserSessionService {
	private static readonly PreferencesKeyPrefix = 'carehome-preferences-';

	constructor(private pushIdentityService: PushIdentityService) { }

	public clearUserSession(): void {
		// Preferences must go before id_token: their key is derived from the user id, so once
		// that is gone the entries can no longer be located and would linger forever.
		this.clearUserCarehomePreferences();

		// Drop the push binding while we still can. Deleting the subscription server-side races with
		// anything already queued for this user; rejecting those on arrival does not. The installation
		// id deliberately survives - it identifies the workstation, not the person.
		this.pushIdentityService.clearBinding();
		localStorage.removeItem('expires_at');
		localStorage.removeItem('id_token');
		localStorage.removeItem('ch_id');
		localStorage.removeItem('type');
		localStorage.removeItem('expire_extension');
		localStorage.removeItem('user_level');
		localStorage.removeItem('carehome-id');
		localStorage.removeItem('token');
		localStorage.removeItem('user_roles');
		localStorage.removeItem('carehome_assignments');
		localStorage.removeItem('selected_carehome');
		// Note: last_used_carehome is intentionally NOT removed to persist across sessions
	}

	public initializeUserSession(userData: UserBasicInfo): void {
		if (!userData) return;
		this.setUserId(userData.Id);
		this.setFullName(userData.Fullname);
		this.setUserRoles(userData.Roles);
		this.setSelectedCarehome(userData.CarehomeId);
		this.setLastUsedCareHome(userData.CarehomeId);
		this.setLanguage(userData.Language);
		if (userData.CarehomeAssignments && userData.CarehomeAssignments.length > 0) {
			this.setCarehomeAssignments(userData.CarehomeAssignments);
		}
	}

	public setUserId(userId: string) {
		localStorage.setItem('id_token', userId);
	}

	public getUserId(): string | null {
		return localStorage.getItem('id_token');
	}

	public setLanguage(language: string): void {
		localStorage.setItem('language', language);
	}

	public getLanguage(): string {
		return localStorage.getItem('language') ?? "en";
	}

	public setUserType(type: string) {
		localStorage.setItem('type', btoa(type));
	}

	public getUserType() {
		return atob(localStorage.getItem('type'));
	}

	public setFullName(fullName: string) {
		return localStorage.setItem('full_name', fullName);
	}

	public getFullName(): string | null {
		return localStorage.getItem('full_name');
	}

	public validateAuthToken(): boolean {
		return this.getUserId() !== null;
	}

	public setUserRoles(roles: string[]): void {
		localStorage.setItem('user_roles', JSON.stringify(roles));
	}

	public getUserRoles(): string[] {
		return JSON.parse(localStorage.getItem('user_roles'));
	}

	public setCarehomeAssignments(assignments: any[]): void {
		localStorage.setItem('carehome_assignments', JSON.stringify(assignments));
	}

	public getCarehomeAssignments(): any[] {
		const assignments = localStorage.getItem('carehome_assignments');
		return assignments ? JSON.parse(assignments) : [];
	}

	public setSelectedCarehome(careHomeId: string | null): void {
		if (careHomeId) {
			localStorage.setItem('selected_carehome', careHomeId);
		} else {
			localStorage.removeItem('selected_carehome');
		}
	}

	public getSelectedCarehome(): string | null {
		return localStorage.getItem('selected_carehome');
	}

	public setLastUsedCareHome(careHomeId: string): void {
		localStorage.setItem('last_used_carehome', careHomeId);
	}

	public getLastUsedCareHome(): string | null {
		return localStorage.getItem('last_used_carehome');
	}


	// Preferences are scoped to carehome *and* user. Carehome terminals are routinely shared, so
	// one user's saved filters must never surface for whoever signs in next. Global admins have no
	// selected carehome and get their own bucket rather than being dropped on the floor.
	private getPreferencesKey(): string | null {
		const userId = this.getUserId();
		if (!userId) {
			return null;
		}

		const carehomeScope = this.getSelectedCarehome() ?? 'global-admin';
		return `${UserSessionService.PreferencesKeyPrefix}${carehomeScope}-${userId}`;
	}

	public setUserCarehomePreferences(preferences: { [key: string]: any }): void {
		const key = this.getPreferencesKey();
		if (!key) {
			return;
		}

		const updatedPreferences = { ...this.getUserCarehomePreferences(), ...preferences };
		localStorage.setItem(key, JSON.stringify(updatedPreferences));
	}

	// Always an object, never null - callers index straight into the result.
	public getUserCarehomePreferences(): { [key: string]: any } {
		const key = this.getPreferencesKey();
		if (!key) {
			return {};
		}

		const preferences = localStorage.getItem(key);
		if (!preferences) {
			return {};
		}

		// A hand-edited or half-written blob must not take down every screen that reads preferences.
		try {
			const parsed = JSON.parse(preferences);
			return parsed && typeof parsed === 'object' ? parsed : {};
		} catch {
			return {};
		}
	}

	public setUserCarehomePreference(name: string, value: any): void {
		this.setUserCarehomePreferences({ [name]: value });
	}

	public getUserCarehomePreference<T>(name: string): T | null {
		const preferences = this.getUserCarehomePreferences();
		return name in preferences ? preferences[name] as T : null;
	}

	// Clears every scope, not just the active one: a session can switch carehomes, and on a shared
	// terminal none of it should outlive logout. Also sweeps up keys written in the pre-user-scoped format.
	private clearUserCarehomePreferences(): void {
		const staleKeys: string[] = [];
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key && key.startsWith(UserSessionService.PreferencesKeyPrefix)) {
				staleKeys.push(key);
			}
		}

		staleKeys.forEach(key => localStorage.removeItem(key));
	}

	public userHasRole(role: string): boolean {
		const userRoles = this.getUserRoles();
		return userRoles ? userRoles.includes(role) : false;
	}

	public hasMinimumRole(role: string): boolean {
		const minimumRole = RolesHierarchy.get(role);
		const userMaximumRole = this.getUserMaximumRole();

		return userMaximumRole >= minimumRole;
	}

	public getDefaultLandingPageByRole(): string {
		if (this.userHasRole(Roles.Admin)) {
			return "/carehomes";
		}

		if (this.userHasRole(Roles.Administrator)
			|| this.userHasRole(Roles.User)) {
			return "/statusoverview/statusoverview";
		}

		if (this.userHasRole(Roles.CarehomeEditor)) {
			return "/carehome/manage-carehome";
		}

		if (this.userHasRole(Roles.ResidentViewer)
			|| this.userHasRole(Roles.ResidentEditor)) {
			return "/citizen/manage-citizens";
		}

		return "/no-access";
	}

	private getUserMaximumRole(): number {
		const userRoles = this.getUserRoles();
		if (!userRoles || userRoles.length === 0) {
			return -1; // No roles assigned, return lowest level
		}
		const maxRole = userRoles.reduce((maxRole, currentRole) => {
			if (RolesHierarchy.get(currentRole) > RolesHierarchy.get(maxRole)) {
				return currentRole;
			} else {
				return maxRole;
			}
		}, userRoles[0]);

		return RolesHierarchy.get(maxRole);
	}
}
